import { useAppDispatch } from '@/state/hooks';
import { logout as logoutAction } from '@/state/slices/session.slice';
import { env } from '@/util/env.utils';
import { callLogout } from '@/util/requests.functions';
import * as amplitude from '@amplitude/analytics-browser';
import { useApolloClient } from '@apollo/client';
import { deleteCookie } from 'cookies-next';
import { useRouter } from 'next/router';

export const useLogout = () => {
    const client = useApolloClient();
    const dispatch = useAppDispatch();
    const router = useRouter();

    const logout = async () => {
        try {
            await callLogout();
        } catch (e) {
            console.error('Error logging out');
            console.error(e);
        }

        // Add Intercom logout
        // try {
        //   // Shutdown Intercom session
        //   await new Promise<void>((resolve) => {
        //     window.Intercom('shutdown');
        //     // shutdown command returns nothing. Timeout is added to allow Intercom time to log out before anything else happens
        //     setTimeout(() => resolve(), 300);
        //   });
        // } catch (error) {
        //   console.error('Error during Intercom logout:', error);
        // }

        // Delete cookie first so that if any redux/apollo requests happen they won't have a cookie and it fails fast
        deleteCookie(env('NEXT_PUBLIC_JWT_COOKIE_NAME'), {
            domain: env('NEXT_PUBLIC_COOKIE_DOMAIN'),
        });
        dispatch(logoutAction());
        amplitude.reset();
        await client.resetStore();
        try {
            // This should almost never throw an error as the function handles errors internally,
            // but we keep this try/catch as a safety net for unexpected issues
            await clearStorageOnLogout();
        } catch (e) {
            console.error('Error clearing local storage keys', e);
            // Continue with logout process despite the error
        }

        await router.push('/login');
    };

    return {
        logout,
    };
};
export function clearStorageOnLogout (): Promise<void> {
    return new Promise((resolve, reject) => {
        try {
            // Safe check for browser environment
            if (typeof window === 'undefined') {
                resolve();
                return;
            }

            // List of keys to clear
            const keysToRemove = [
                'selectedFilters',
                'sortingInput',
                'sortDirection',
            ];

            // Function to dispatch storage events
            const dispatchStorageEvent = (key: string, newValue: string | null) => {
                window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
            };

            // Process each key
            keysToRemove.forEach(key => {
                // Remove from localStorage - this is safe even if key doesn't exist
                window.localStorage.removeItem(key);

                // Dispatch event regardless of whether the key existed
                // This ensures any components using the custom hooks re-render appropriately
                dispatchStorageEvent(key, null);
            });

            resolve();
        } catch (error) {
            // Log the error for debugging but resolve the promise anyway
            console.error('Error during localStorage cleanup:', error);
            resolve(); // Still resolve to not block logout flow
        }
    });
}
